import React, { useState, useRef } from 'react';
import './FormVacancyCustom.scss';
import './FormVacancyCustom-media.scss';
import Input from '../Input/Input';
import InputPhone from '../Input/InputPhone';
import InputFile from '../Input/InputFile';
import FormContainer from '../FormContainer/FormContainer';
import { ServiceData } from '../../redux/services/ServiceRedux';
import { connect, WithStore, MapStateToProps, MapDispatchToProps } from '../../redux/services/Imports';
import { ValidationFormItems } from '../../services/GlobalFunctions';
import { useSmartCaptcha } from '../SmartCaptcha/useSmartCaptcha';
import SmartCaptchaBox from '../SmartCaptcha/SmartCaptchaBox';
import { Props, FieldConfig } from './interfaces';

const FormVacancyCustom = ({
  blockedSend,
  blockedSendLoaded,
  eventMetrika,
  contactFormId,
  contactForms,
  formFields,
  title,
  subTitle,
  buttonText,
  vacancyTitle,
  widthWindow,
}: Props) => {
  const [formData, setFormData] = useState<{ [key: string]: any }>({});
  const [errors, setErrors] = useState<{ [key: string]: string | null }>({});
  const [successShow, setSuccessShow] = useState(false);
  const [serverText, setServerText] = useState<string | null>(null);
  const [disableBtn, setDisableBtn] = useState(false);
  const [showBlockedError, setShowBlockedError] = useState(false);

  const captcha = useSmartCaptcha(contactForms);

  const handleFieldChange = (fieldName: string, value: any) => {
    setFormData({ ...formData, [fieldName]: value });
    // Clear error when user starts typing
    if (errors[fieldName]) {
      setErrors({ ...errors, [fieldName]: null });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string | null } = {};
    let isValid = true;

    formFields.forEach((field: FieldConfig) => {
      if (field.field_required && !formData[field.field_name]) {
        newErrors[field.field_name] = 'Это поле обязательно';
        isValid = false;
      }

      // Validate phone
      if (field.field_type === 'phone' && formData[field.field_name]) {
        const phone = formData[field.field_name];
        if (!ValidationFormItems({ phone, setErrorPhone: (err: string | null) => {
          newErrors[field.field_name] = err;
          if (err) isValid = false;
        }})) {
          isValid = false;
        }
      }

      // Validate email
      if ((field.field_type === 'email' || field.field_validation === 'email') && formData[field.field_name]) {
        const email = formData[field.field_name];
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
          newErrors[field.field_name] = 'Неверный формат email';
          isValid = false;
        }
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  const success = () => {
    setFormData({});
    captcha.reset();
    setSuccessShow(true);
    if (typeof ym !== 'undefined') {
      ym(26501373, 'reachGoal', eventMetrika || 'vacancy_form_custom_success');
    }
    setTimeout(() => setSuccessShow(false), 3000);
    setDisableBtn(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setDisableBtn(true);
    setShowBlockedError(false);

    if (blockedSend.state) {
      setShowBlockedError(true);
      setDisableBtn(false);
      return;
    }

    if (!validateForm()) {
      setDisableBtn(false);
      return;
    }

    let token = '';
    try {
      token = await captcha.ensureToken();
    } catch (err) {
      // Текст ошибки пользователю показывает SmartCaptchaBox (captchaError),
      // здесь логируем причину — иначе «форма молчит» не отличить от
      // просроченного токена, закрытого челленджа или недоступной капчи.
      console.error('[vacancy-form] отправка отменена: капча не выдала токен', {
        reason: (err as any)?.message ?? String(err),
      });
      setDisableBtn(false);
      return;
    }

    // Prepare data for submission
    const submitData: { [key: string]: any } = {
      ...formData,
      vacancy_title: vacancyTitle,
      title: title,
    };

    ServiceData.submitForm({
      data: submitData,
      token,
      setServerText: (t: string) => { setServerText(t); captcha.reset(); setDisableBtn(false); },
      success,
      blockedSendLoaded,
      eventMetrika: eventMetrika || 'vacancy_form_custom',
      contactFormId: contactFormId,
    });
  };

  const renderField = (field: FieldConfig, index: number) => {
    const commonProps = {
      key: index,
      name: field.field_name,
      label: field.field_label,
      placeholder: field.field_placeholder || '',
      error: errors[field.field_name],
      widthWindow,
    };

    switch (field.field_type) {
      case 'text':
        return (
          <Input
            {...commonProps}
            value={formData[field.field_name] || ''}
            onChange={(e: any) => handleFieldChange(field.field_name, e.target.value)}
          />
        );

      case 'email':
        return (
          <Input
            {...commonProps}
            type="email"
            value={formData[field.field_name] || ''}
            onChange={(e: any) => handleFieldChange(field.field_name, e.target.value)}
          />
        );

      case 'phone':
        return (
          <InputPhone
            {...commonProps}
            value={formData[field.field_name] || '+7'}
            onChange={(e: any) => handleFieldChange(field.field_name, e.target.value)}
          />
        );

      case 'textarea':
        return (
          <div key={index} className="input">
            {field.field_label ? <p className="input__label">{field.field_label}</p> : null}
            <textarea
              name={field.field_name}
              className={`input__field${errors[field.field_name] ? ' input__field_error' : ''}`}
              placeholder={field.field_placeholder || ''}
              value={formData[field.field_name] || ''}
              onChange={(e) => handleFieldChange(field.field_name, e.target.value)}
              rows={4}
            />
            {errors[field.field_name] ? <span className="input__error">{errors[field.field_name]}</span> : null}
          </div>
        );

      case 'file':
        return (
          <InputFile
            {...commonProps}
            onChange={(file: File | null) => handleFieldChange(field.field_name, file)}
            accept={field.field_accept || '.pdf,.doc,.docx'}
            maxSize={field.field_max_size || 5}
            required={field.field_required}
          />
        );

      case 'checkbox':
        return (
          <div key={index} className="input">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name={field.field_name}
                checked={formData[field.field_name] || false}
                onChange={(e) => handleFieldChange(field.field_name, e.target.checked)}
              />
              <span>{field.field_label}</span>
            </label>
            {errors[field.field_name] ? <span className="input__error">{errors[field.field_name]}</span> : null}
          </div>
        );

      case 'select':
        return (
          <div key={index} className="input">
            {field.field_label ? <p className="input__label">{field.field_label}{field.field_required ? ' *' : ''}</p> : null}
            <select
              name={field.field_name}
              className={`input__field${errors[field.field_name] ? ' input__field_error' : ''}`}
              value={formData[field.field_name] || ''}
              onChange={(e) => handleFieldChange(field.field_name, e.target.value)}
            >
              <option value="">{field.field_placeholder || 'Выберите опцию'}</option>
              {field.field_options?.map((option, optIndex) => (
                <option key={optIndex} value={option.option_value}>
                  {option.option_label}
                </option>
              ))}
            </select>
            {errors[field.field_name] ? <span className="input__error">{errors[field.field_name]}</span> : null}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <FormContainer
      title={title}
      subTitle={subTitle}
      buttonText={buttonText}
      form={<>{formFields.map((field: FieldConfig, index: number) => renderField(field, index))}<SmartCaptchaBox captcha={captcha} /></>}
      onSubmit={handleSubmit}
      serverText={serverText}
      successShow={successShow}
      disableBtn={disableBtn}
      showBlockedError={showBlockedError}
      widthWindow={widthWindow}
    />
  );
};

// ownProps include contactForms from PHP data-props; let them override Redux defaults
const mergeProps = (stateProps: any, dispatchProps: any, ownProps: any) => ({
  ...stateProps,
  ...dispatchProps,
  ...ownProps,
});

export default WithStore()(connect(MapStateToProps, MapDispatchToProps, mergeProps)(FormVacancyCustom));
